/* quick-weight.js
   Quick Weight modal:
   open -> load product JSON -> pick variant -> add to cart (AJAX)

   Also refreshes theme mini-cart widget (.mini-cart-content)
   so drawer updates immediately.
*/

(() => {
  "use strict";

  /* =========================
     DOM helpers
  ========================= */

  const $ = (sel, root = document) =>
    root.querySelector(sel);

  const $$ = (sel, root = document) =>
    Array.from(root.querySelectorAll(sel));

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /* =========================
     Money helpers
  ========================= */

  function formatMoney(cents) {
    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === "function"
    ) {
      const fmt =
        window.theme?.moneyFormat ||
        window.Shopify?.money_format ||
        "${{amount}}";

      return window.Shopify.formatMoney(
        cents,
        fmt
      );
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  /* =========================
     Cart + mini-cart widget
  ========================= */

  async function fetchCart() {
    const res = await fetch(
      "/cart.js",
      {
        credentials: "same-origin",
        headers: {
          Accept: "application/json"
        }
      }
    );

    if (!res.ok) {
      throw new Error(
        "Failed to fetch cart.js"
      );
    }

    return res.json();
  }

  function miniCartRoot() {
    return document.querySelector(
      ".mini-cart-content"
    );
  }

  function updateMiniCartDOM(cart) {
    $$(".js-cart-count").forEach(
      (el) => {
        el.textContent =
          cart.item_count;
      }
    );

    $$(".js-cart-total").forEach(
      (el) => {
        el.textContent =
          formatMoney(
            cart.total_price
          );
      }
    );

    const root =
      miniCartRoot();

    if (!root) return;

    const list =
      $(".js-mini-cart-content", root);

    if (!list) return;

    if (
      !cart.items ||
      cart.items.length === 0
    ) {
      list.innerHTML = `
        <div class="py-4">
          Your cart is empty
        </div>
      `;

      document.dispatchEvent(
        new Event("cart:updated")
      );

      document.dispatchEvent(
        new Event("cart:change")
      );

      return;
    }

    list.innerHTML =
      cart.items
        .map(
          (item, idx) => {
            const line =
              idx + 1;

            const url =
              item.url || "#";

            const title =
              item.product_title ||
              item.title ||
              "Item";

            const variantTitle =
              item.variant_title &&
              item.variant_title !==
                "Default Title"
                ? ` - ${item.variant_title}`
                : "";

            const priceEach =
              formatMoney(
                item.final_price
              );

            const qty =
              item.quantity;

            return `
<div class="mini-cart-item">

  <a
    class="mini-cart-image"
    href="${url}"
  >
    ${
      item.image
        ? `
          <img
            src="${item.image}"
            alt="${escapeHtml(title)}"
          >
        `
        : ""
    }
  </a>

  <div class="mini-cart-meta">

    <div class="mb-1">
      <a href="${url}">
        ${escapeHtml(title)}
        ${escapeHtml(variantTitle)}
      </a>
    </div>

    <span>${priceEach}</span>
    x ${qty}

    <div
      class="
        d-flex
        align-items-center
        mt-2
        justify-content-between
      "
    >

      <div class="js-qty mt-1">

        <input
          data-line="${line}"
          type="text"
          min="0"
          class="
            change-minicart
            js-qty__input
          "
          pattern="[0-9]*"
          value="${qty}"
        >

        <button
          type="button"
          class="
            js-qty__adjust
            js-qty__minus
          "
          aria-label="
            Reduce item quantity
            by one
          "
        >
          −
        </button>

        <button
          type="button"
          class="
            js-qty__adjust
            js-qty__plus
          "
          aria-label="
            Increase item quantity
            by one
          "
        >
          +
        </button>

      </div>

      <button
        class="
          btn
          js-remove-mini-cart
          link
          pr-0
        "
        data-id="${item.variant_id}"
      >
        Remove
      </button>

    </div>

  </div>

</div>
`;
          }
        )
        .join("");

    document.dispatchEvent(
      new Event("cart:updated")
    );

    document.dispatchEvent(
      new Event("cart:change")
    );
  }

  async function refreshMiniCart() {
    const cart =
      await fetchCart();

    $$(".js-cart-count").forEach(
      (el) => {
        el.textContent =
          cart.item_count;
      }
    );

    $$(".js-cart-total").forEach(
      (el) => {
        el.textContent =
          formatMoney(
            cart.total_price
          );
      }
    );

    if (miniCartRoot()) {
      updateMiniCartDOM(cart);
    }
  }

  async function cartChangeByLine(
    line,
    quantity
  ) {
    const res =
      await fetch(
        "/cart/change.js",
        {
          method: "POST",

          credentials:
            "same-origin",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json"
          },

          body:
            JSON.stringify({
              line,
              quantity
            })
        }
      );

    if (!res.ok) {
      throw new Error(
        "cart/change.js failed"
      );
    }

    return res.json();
  }

  async function cartChangeByVariant(
    variantId,
    quantity
  ) {
    const res =
      await fetch(
        "/cart/change.js",
        {
          method: "POST",

          credentials:
            "same-origin",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json"
          },

          body:
            JSON.stringify({
              id: variantId,
              quantity
            })
        }
      );

    if (!res.ok) {
      throw new Error(
        "cart/change.js failed"
      );
    }

    return res.json();
  }

  /* =========================
     Mini-cart quantity
  ========================= */

  document.addEventListener(
    "click",
    async (e) => {
      if (!miniCartRoot()) {
        return;
      }

      const plus =
        e.target.closest(
          ".js-qty__plus"
        );

      const minus =
        e.target.closest(
          ".js-qty__minus"
        );

      if (
        !plus &&
        !minus
      ) {
        return;
      }

      const wrap =
        e.target.closest(
          ".js-qty"
        );

      const input =
        wrap?.querySelector(
          "input.change-minicart[data-line]"
        );

      if (!input) return;

      e.preventDefault();

      const line =
        parseInt(
          input.getAttribute(
            "data-line"
          ),
          10
        );

      const current =
        parseInt(
          input.value || "0",
          10
        );

      const next =
        Math.max(
          0,
          current +
            (plus ? 1 : -1)
        );

      input.value =
        String(next);

      try {
        await cartChangeByLine(
          line,
          next
        );

        await refreshMiniCart();

      } catch (err) {
        console.warn(
          "[QW] mini cart qty update failed",
          err
        );
      }
    }
  );

  /* =========================
     Mini-cart remove
  ========================= */

  document.addEventListener(
    "click",
    async (e) => {
      if (!miniCartRoot()) {
        return;
      }

      const btn =
        e.target.closest(
          ".js-remove-mini-cart"
        );

      if (!btn) {
        return;
      }

      e.preventDefault();

      const variantId =
        parseInt(
          btn.getAttribute(
            "data-id"
          ),
          10
        );

      if (!variantId) {
        return;
      }

      try {
        await cartChangeByVariant(
          variantId,
          0
        );

        await refreshMiniCart();

      } catch (err) {
        console.warn(
          "[QW] mini cart remove failed",
          err
        );
      }
    }
  );

  /* =========================
     Quick Weight modal
  ========================= */

  const modal =
    $("#QuickWeightModal");

  if (!modal) {
    console.warn(
      "[QW] #QuickWeightModal not found"
    );

    return;
  }

  const elTitle =
    $(
      "[data-qw-title]",
      modal
    );

  const elMedia =
    $(
      "[data-qw-media]",
      modal
    );

  const elDesc =
    $(
      "[data-qw-desc]",
      modal
    );

  const elBody =
    $(
      "[data-qw-body]",
      modal
    );

  const btnAdd =
    $(
      "[data-qw-add]",
      modal
    );

  let state = {
    product: null,
    productUrl: null,
    selectedVariantId: null,
    thc: "",
    thcBar: "",
    effects: "",
    flavors: ""
  };

  /* =========================
     Open / close
  ========================= */

  function openModal() {
    modal.classList.add(
      "is-open"
    );

    modal.style.display =
      "block";

    modal.setAttribute(
      "aria-hidden",
      "false"
    );

    document
      .documentElement
      .classList
      .add(
        "qw-modal-open"
      );
  }

  function closeModal() {
    modal.classList.remove(
      "is-open"
    );

    modal.style.display =
      "";

    modal.setAttribute(
      "aria-hidden",
      "true"
    );

    document
      .documentElement
      .classList
      .remove(
        "qw-modal-open"
      );
  }

  /* =========================
     Loading / add button
  ========================= */

  function setLoading(
    loading
  ) {
    if (!elBody) {
      return;
    }

    if (loading) {
      elBody.innerHTML = `
        <div
          class="
            quick-weight-modal__loading
          "
        >
          Loading…
        </div>
      `;
    }
  }

  function setAddEnabled(
    enabled
  ) {
    if (!btnAdd) {
      return;
    }

    btnAdd.disabled =
      !enabled;
  }

  /* =========================
     Product helpers
  ========================= */

  function normalizeProductUrl(
    url
  ) {
    if (!url) {
      return null;
    }

    return url;
  }

  function getBestImage(
    product
  ) {
    if (
      product.featured_image
    ) {
      return product.featured_image;
    }

    if (
      product.images &&
      product.images.length
    ) {
      return product.images[0];
    }

    return null;
  }

  function getShortDescription(
    product
  ) {
    const html =
      product.description || "";

    const tmp =
      document.createElement(
        "div"
      );

    tmp.innerHTML =
      html;

    return (
      tmp.textContent ||
      tmp.innerText ||
      ""
    ).trim();
  }

  /* =========================
     THC helpers
  ========================= */

  function clampPercent(value) {
    const num =
      parseFloat(
        String(value || "")
          .replace("%", "")
          .trim()
      );

    if (
      !Number.isFinite(num)
    ) {
      return 0;
    }

    return Math.max(
      0,
      Math.min(
        100,
        num
      )
    );
  }

  /* =========================
     Quick Weight image/details
  ========================= */

  function styleQuickWeightTop() {
    const wrap =
      $(".qw-media-wrap", modal);

    const img =
      $(".qw-media-img", modal);

    const details =
      $(".qw-thc-details", modal);

    const isMobile =
      window.matchMedia(
        "(max-width: 767px)"
      ).matches;

    if (wrap) {
      wrap.style.setProperty(
        "width",
        "100%",
        "important"
      );

      wrap.style.setProperty(
        "max-width",
        isMobile
          ? "230px"
          : "300px",
        "important"
      );

      wrap.style.setProperty(
        "height",
        isMobile
          ? "190px"
          : "240px",
        "important"
      );

      wrap.style.setProperty(
        "aspect-ratio",
        "auto",
        "important"
      );

      wrap.style.setProperty(
        "margin",
        "0 auto",
        "important"
      );

      wrap.style.setProperty(
        "border-radius",
        "14px",
        "important"
      );

      wrap.style.setProperty(
        "overflow",
        "hidden",
        "important"
      );

      wrap.style.setProperty(
        "background",
        "#f4f4f4",
        "important"
      );
    }

    if (img) {
      img.style.setProperty(
        "width",
        "100%",
        "important"
      );

      img.style.setProperty(
        "height",
        "100%",
        "important"
      );

      img.style.setProperty(
        "object-fit",
        "cover",
        "important"
      );

      img.style.setProperty(
        "display",
        "block",
        "important"
      );
    }

    if (details) {
      details.style.setProperty(
        "max-width",
        isMobile
          ? "100%"
          : "520px",
        "important"
      );

      details.style.setProperty(
        "margin",
        "10px auto 0",
        "important"
      );
    }
  }

  /* =========================
     Product top area
  ========================= */

  function renderTop(
    product
  ) {
    if (elTitle) {
      elTitle.textContent =
        product.title ||
        "Select Weight";
    }

    const imgSrc =
      getBestImage(
        product
      );

    const thc =
      String(
        state.thc || ""
      ).trim();

    const thcBar =
      clampPercent(
        state.thcBar
      );

    const effects =
      String(
        state.effects || ""
      ).trim();

    const flavors =
      String(
        state.flavors || ""
      ).trim();

    const hasDetails =
      !!(
        thc ||
        effects ||
        flavors
      );

    if (elMedia) {
      elMedia.innerHTML = `
        ${
          imgSrc
            ? `
              <div
                class="qw-media-wrap"
              >
                <img
                  class="qw-media-img"
                  src="${imgSrc}"
                  alt="${escapeHtml(
                    product.title
                  )}"
                >
              </div>
            `
            : ""
        }

        ${
          hasDetails
            ? `
<div
  class="qw-thc-details"
  style="
    width:100%;
    box-sizing:border-box;
    padding:10px 12px 11px;
    background:#f7f6f1;
    border:1px solid #d9d8d1;
    border-radius:10px;
    color:#183d28;
  "
>

  ${
    thc
      ? `
        <div
          style="
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:10px;
            margin-bottom:7px;
          "
        >
          <span
            style="
              font-size:15px;
              font-weight:700;
              line-height:1;
              color:#183d28;
            "
          >
            THC
          </span>

          <span
            style="
              font-size:14px;
              font-weight:700;
              line-height:1;
              color:#183d28;
            "
          >
            ${escapeHtml(thc)}
          </span>
        </div>

        <div
          style="
            width:100%;
            height:8px;
            overflow:hidden;
            background:#ddded8;
            border-radius:999px;
          "
          role="progressbar"
          aria-label="THC potency"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow="${thcBar}"
        >
          <span
            style="
              display:block;
              width:${thcBar}%;
              height:100%;
              background:linear-gradient(
                90deg,
                #ff4d3d 0%,
                #ff8a45 100%
              );
              border-radius:999px;
            "
          ></span>
        </div>

        ${
          effects || flavors
            ? `
              <div
                style="
                  width:100%;
                  height:1px;
                  margin:10px 0 9px;
                  background:#d9d8d1;
                "
              ></div>
            `
            : ""
        }
      `
      : ""
  }

  ${
    effects || flavors
      ? `
        <div
          style="
            display:grid;
            grid-template-columns:
              minmax(0,1fr) minmax(0,1fr);
            align-items:start;
          "
        >

          <div
            style="
              padding-right:12px;
            "
          >
            ${
              effects
                ? `
                  <div
                    style="
                      margin-bottom:3px;
                      color:#8b9a8b;
                      font-size:10px;
                      font-weight:700;
                      line-height:1;
                      letter-spacing:.03em;
                      text-transform:uppercase;
                    "
                  >
                    Effects
                  </div>

                  <div
                    style="
                      color:#183d28;
                      font-size:11px;
                      font-weight:500;
                      line-height:1.3;
                    "
                  >
                    ${escapeHtml(effects)}
                  </div>
                `
                : ""
            }
          </div>


          <div
            style="
              min-height:31px;
              padding-left:12px;
              border-left:1px solid #d9d8d1;
            "
          >
            ${
              flavors
                ? `
                  <div
                    style="
                      margin-bottom:3px;
                      color:#8b9a8b;
                      font-size:10px;
                      font-weight:700;
                      line-height:1;
                      letter-spacing:.03em;
                      text-transform:uppercase;
                    "
                  >
                    Flavors
                  </div>

                  <div
                    style="
                      color:#183d28;
                      font-size:11px;
                      font-weight:500;
                      line-height:1.3;
                    "
                  >
                    ${escapeHtml(flavors)}
                  </div>
                `
                : ""
            }
          </div>

        </div>
      `
      : ""
  }

</div>
            `
            : ""
        }
      `;

      requestAnimationFrame(
        styleQuickWeightTop
      );

      setTimeout(
        styleQuickWeightTop,
        30
      );
    }

    if (elDesc) {
      elDesc.textContent =
        getShortDescription(
          product
        ) || "";
    }
  }

  /* =========================
     Variant helper
  ========================= */

  function variantLabel(v) {
    const name =
      v.option1 ||
      v.title ||
      "Option";

    const price =
      typeof v.price ===
        "number"
        ? formatMoney(
            v.price
          )
        : "";

    return {
      name,
      price
    };
  }

  /* =========================
     Variant card styling
  ========================= */

  function styleVariantCards() {
    const grid =
      $(".qw-variant-grid", modal);

    if (!grid) {
      return;
    }

    const isSmallPhone =
      window.matchMedia(
        "(max-width: 390px)"
      ).matches;

    const isMobile =
      window.matchMedia(
        "(max-width: 767px)"
      ).matches;

    const columns =
      isSmallPhone
        ? 2
        : isMobile
          ? 3
          : 5;

    grid.style.setProperty(
      "display",
      "grid",
      "important"
    );

    grid.style.setProperty(
      "grid-template-columns",
      `repeat(${columns}, minmax(0, 1fr))`,
      "important"
    );

    grid.style.setProperty(
      "gap",
      isMobile
        ? "7px"
        : "8px",
      "important"
    );

    grid.style.setProperty(
      "width",
      "100%",
      "important"
    );

    grid.style.setProperty(
      "margin",
      "0",
      "important"
    );

    grid.style.setProperty(
      "padding",
      isMobile
        ? "7px 0 2px"
        : "8px 0 2px",
      "important"
    );

    grid.style.setProperty(
      "border",
      "0",
      "important"
    );

    grid.style.setProperty(
      "overflow",
      "visible",
      "important"
    );

    $$(
      ".qw-variant-option",
      grid
    ).forEach(
      (option) => {
        option.style.setProperty(
          "position",
          "relative",
          "important"
        );

        option.style.setProperty(
          "display",
          "block",
          "important"
        );

        option.style.setProperty(
          "width",
          "100%",
          "important"
        );

        option.style.setProperty(
          "min-width",
          "0",
          "important"
        );

        option.style.setProperty(
          "margin",
          "0",
          "important"
        );

        option.style.setProperty(
          "padding",
          "0",
          "important"
        );
      }
    );

    $$(
      ".qw-variant-input",
      grid
    ).forEach(
      (input) => {
        input.style.setProperty(
          "position",
          "absolute",
          "important"
        );

        input.style.setProperty(
          "width",
          "1px",
          "important"
        );

        input.style.setProperty(
          "height",
          "1px",
          "important"
        );

        input.style.setProperty(
          "opacity",
          "0",
          "important"
        );

        input.style.setProperty(
          "pointer-events",
          "none",
          "important"
        );
      }
    );

    $$(
      ".qw-variant-label",
      grid
    ).forEach(
      (label) => {
        const variantId =
          parseInt(
            label.getAttribute(
              "data-variant-id"
            ),
            10
          );

        const selected =
          variantId ===
          state.selectedVariantId;

        label.style.setProperty(
          "display",
          "flex",
          "important"
        );

        label.style.setProperty(
          "flex-direction",
          "column",
          "important"
        );

        label.style.setProperty(
          "flex-wrap",
          "nowrap",
          "important"
        );

        label.style.setProperty(
          "align-items",
          "center",
          "important"
        );

        label.style.setProperty(
          "justify-content",
          "center",
          "important"
        );

        label.style.setProperty(
          "gap",
          "3px",
          "important"
        );

        label.style.setProperty(
          "width",
          "100%",
          "important"
        );

        label.style.setProperty(
          "min-width",
          "0",
          "important"
        );

        label.style.setProperty(
          "height",
          isMobile
            ? "60px"
            : "64px",
          "important"
        );

        label.style.setProperty(
          "min-height",
          isMobile
            ? "60px"
            : "64px",
          "important"
        );

        label.style.setProperty(
          "max-height",
          isMobile
            ? "60px"
            : "64px",
          "important"
        );

        label.style.setProperty(
          "margin",
          "0",
          "important"
        );

        label.style.setProperty(
          "padding",
          isMobile
            ? "7px 4px"
            : "8px 5px",
          "important"
        );

        label.style.setProperty(
          "box-sizing",
          "border-box",
          "important"
        );

        label.style.setProperty(
          "color",
          "#183d28",
          "important"
        );

        label.style.setProperty(
          "background",
          selected
            ? "#e7efe2"
            : "#f7f5ef",
          "important"
        );

        label.style.setProperty(
          "border",
          selected
            ? "2px solid #315f36"
            : "1px solid rgba(24, 61, 40, 0.18)",
          "important"
        );

        label.style.setProperty(
          "border-radius",
          "9px",
          "important"
        );

        label.style.setProperty(
          "box-shadow",
          selected
            ? "0 0 0 2px rgba(49, 95, 54, 0.08)"
            : "none",
          "important"
        );

        label.style.setProperty(
          "transform",
          "none",
          "important"
        );

        label.style.setProperty(
          "cursor",
          "pointer",
          "important"
        );

        label.style.setProperty(
          "text-align",
          "center",
          "important"
        );

        const name =
          $(".opt-name", label);

        const price =
          $(".opt-price", label);

        if (name) {
          name.style.setProperty(
            "display",
            "block",
            "important"
          );

          name.style.setProperty(
            "width",
            "100%",
            "important"
          );

          name.style.setProperty(
            "margin",
            "0",
            "important"
          );

          name.style.setProperty(
            "padding",
            "0",
            "important"
          );

          name.style.setProperty(
            "color",
            "#183d28",
            "important"
          );

          name.style.setProperty(
            "font-size",
            isMobile
              ? "12px"
              : "13px",
            "important"
          );

          name.style.setProperty(
            "font-weight",
            "700",
            "important"
          );

          name.style.setProperty(
            "line-height",
            "1.1",
            "important"
          );

          name.style.setProperty(
            "text-align",
            "center",
            "important"
          );

          name.style.setProperty(
            "white-space",
            "nowrap",
            "important"
          );
        }

        if (price) {
          price.style.setProperty(
            "display",
            "block",
            "important"
          );

          price.style.setProperty(
            "width",
            "100%",
            "important"
          );

          price.style.setProperty(
            "margin",
            "0",
            "important"
          );

          price.style.setProperty(
            "padding",
            "0",
            "important"
          );

          price.style.setProperty(
            "color",
            "rgba(24, 61, 40, 0.72)",
            "important"
          );

          price.style.setProperty(
            "font-size",
            isMobile
              ? "10px"
              : "11px",
            "important"
          );

          price.style.setProperty(
            "font-weight",
            "500",
            "important"
          );

          price.style.setProperty(
            "line-height",
            "1.1",
            "important"
          );

          price.style.setProperty(
            "text-align",
            "center",
            "important"
          );

          price.style.setProperty(
            "white-space",
            "nowrap",
            "important"
          );

          price.style.setProperty(
            "opacity",
            "1",
            "important"
          );
        }
      }
    );
  }

  /* =========================
     Render variants
     Cheapest first
  ========================= */

  function renderVariants(
    product
  ) {
    if (!elBody) {
      return;
    }

    const variants =
      (
        product.variants ||
        []
      )
        .filter(
          (v) =>
            v.available
        )
        .sort(
          (a, b) =>
            a.price - b.price
        );

    if (
      !variants.length
    ) {
      elBody.innerHTML = `
        <div class="py-2">
          No weights available.
        </div>
      `;

      setAddEnabled(
        false
      );

      state.selectedVariantId =
        null;

      return;
    }

    const pre =
      variants[0];

    state.selectedVariantId =
      pre.id;

    setAddEnabled(
      true
    );

    elBody.innerHTML = `
      <fieldset
        class="
          single-option-radio
          qw-variant-grid
        "
        aria-label="
          Weight options
        "
      >

        ${
          variants
            .map(
              (v) => {
                const {
                  name,
                  price
                } =
                  variantLabel(v);

                const normalizedName =
                  String(
                    name || ""
                  )
                    .trim()
                    .toLowerCase()
                    .replace(
                      /\s+/g,
                      " "
                    );

                const isMostPopular =
                  normalizedName ===
                    "1/8 oz" ||
                  normalizedName ===
                    "1/8oz";

                const checked =
                  v.id ===
                  state.selectedVariantId
                    ? " checked"
                    : "";

                return `
                  <div
                    class="
                      qw-variant-option
                    "
                  >

                    ${
                      isMostPopular
                        ? `
                          <span
                            class="
                              qw-most-popular
                            "
                            style="
                              position:absolute !important;
                              top:-4px !important;
                              left:50% !important;
                              transform:translateX(-50%) !important;
                              z-index:5 !important;
                              padding:2px 7px !important;
                              background:#315f36 !important;
                              color:#ffffff !important;
                              border-radius:999px !important;
                              font-size:8px !important;
                              font-weight:700 !important;
                              line-height:1.2 !important;
                              white-space:nowrap !important;
                              box-shadow:0 1px 4px rgba(0,0,0,.08) !important;
                            "
                          >
                            Most Popular
                          </span>
                        `
                        : ""
                    }

                    <input
                      type="radio"
                      class="
                        qw-variant-input
                      "
                      id="
                        qw-variant-${v.id}
                      "
                      name="qw-weight"
                      value="${v.id}"
                      data-variant-id="${v.id}"
                      ${checked}
                    >

                    <label
                      for="
                        qw-variant-${v.id}
                      "
                      class="
                        qw-variant-label
                      "
                      data-qw-variant
                      data-variant-id="${v.id}"
                    >

                      <span
                        class="opt-name"
                      >
                        ${escapeHtml(name)}
                      </span>

                      <span
                        class="opt-price"
                      >
                        ${escapeHtml(price)}
                      </span>

                    </label>

                  </div>
                `;
              }
            )
            .join("")
        }

      </fieldset>
    `;

    requestAnimationFrame(
      styleVariantCards
    );

    setTimeout(
      styleVariantCards,
      30
    );

    setTimeout(
      styleVariantCards,
      150
    );
  }

  /* =========================
     Selection
  ========================= */

  function updateVariantSelection(
    newId
  ) {
    state.selectedVariantId =
      newId;

    setAddEnabled(
      !!newId
    );

    $$(
      ".qw-variant-input",
      modal
    ).forEach(
      (input) => {
        const id =
          parseInt(
            input.getAttribute(
              "data-variant-id"
            ),
            10
          );

        input.checked =
          id === newId;
      }
    );

    styleVariantCards();
  }

  /* =========================
     Load product JSON
  ========================= */

  async function loadProductByUrl(
    productUrl
  ) {
    const jsUrl =
      productUrl
        .replace(
          /\/$/,
          ""
        ) +
      ".js";

    const res =
      await fetch(
        jsUrl,
        {
          credentials:
            "same-origin",

          headers: {
            Accept:
              "application/json"
          }
        }
      );

    if (!res.ok) {
      throw new Error(
        "Failed to fetch product .js"
      );
    }

    return res.json();
  }

  /* =========================
     Add to cart
  ========================= */

  async function addToCart(
    variantId,
    qty = 1
  ) {
    const res =
      await fetch(
        "/cart/add.js",
        {
          method: "POST",

          credentials:
            "same-origin",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json"
          },

          body:
            JSON.stringify({
              id:
                variantId,

              quantity:
                qty
            })
        }
      );

    if (!res.ok) {
      let msg =
        "Add to cart failed";

      try {
        const j =
          await res.json();

        msg =
          j?.description ||
          j?.message ||
          msg;

      } catch (_) {}

      throw new Error(
        msg
      );
    }

    return res.json();
  }

  /* =========================
     Reset modal
  ========================= */

  function resetModalUI() {
    if (elTitle) {
      elTitle.textContent =
        "Select Weight";
    }

    if (elMedia) {
      elMedia.innerHTML =
        "";
    }

    if (elDesc) {
      elDesc.textContent =
        "";
    }

    if (elBody) {
      elBody.innerHTML =
        "";
    }

    setAddEnabled(
      false
    );

    if (btnAdd) {
      btnAdd.textContent =
        "Add to Cart";
    }

    state.thc =
      "";

    state.thcBar =
      "";

    state.effects =
      "";

    state.flavors =
      "";
  }

  /* =========================
     Open product
  ========================= */

  async function openForProduct({
    url,
    thc = "",
    thcBar = "",
    effects = "",
    flavors = ""
  }) {
    resetModalUI();

    state.product =
      null;

    state.productUrl =
      normalizeProductUrl(
        url
      );

    state.selectedVariantId =
      null;

    state.thc =
      thc || "";

    state.thcBar =
      thcBar || "";

    state.effects =
      effects || "";

    state.flavors =
      flavors || "";

    if (
      !state.productUrl
    ) {
      return;
    }

    openModal();

    setLoading(
      true
    );

    try {
      const product =
        await loadProductByUrl(
          state.productUrl
        );

      state.product =
        product;

      renderTop(
        product
      );

      renderVariants(
        product
      );

    } catch (err) {
      console.warn(
        "[QW] load failed",
        err
      );

      if (elBody) {
        elBody.innerHTML = `
          <div class="py-2">
            Could not load product.
          </div>
        `;
      }

      setAddEnabled(
        false
      );
    }
  }

  /* =========================
     Open from product cards
  ========================= */

  document.addEventListener(
    "click",
    (e) => {
      const btn =
        e.target.closest(
          "[data-quick-weight]"
        );

      if (!btn) {
        return;
      }

      e.preventDefault();

      const url =
        btn.getAttribute(
          "data-product-url"
        ) ||
        btn.getAttribute(
          "href"
        );

      openForProduct({
        url,

        thc:
          btn.getAttribute(
            "data-thc"
          ) || "",

        thcBar:
          btn.getAttribute(
            "data-thc-bar"
          ) || "",

        effects:
          btn.getAttribute(
            "data-effects"
          ) || "",

        flavors:
          btn.getAttribute(
            "data-flavors"
          ) || ""
      });
    }
  );

  /* =========================
     Close handlers
  ========================= */

  document.addEventListener(
    "click",
    (e) => {
      if (
        !modal.classList.contains(
          "is-open"
        )
      ) {
        return;
      }

      if (
        e.target.closest(
          "[data-qw-close]"
        )
      ) {
        e.preventDefault();

        closeModal();
      }
    }
  );

  document.addEventListener(
    "keydown",
    (e) => {
      if (
        !modal.classList.contains(
          "is-open"
        )
      ) {
        return;
      }

      if (
        e.key ===
        "Escape"
      ) {
        closeModal();
      }
    }
  );

  /* =========================
     Variant click
  ========================= */

  document.addEventListener(
    "click",
    (e) => {
      if (
        !modal.classList.contains(
          "is-open"
        )
      ) {
        return;
      }

      const vbtn =
        e.target.closest(
          "[data-qw-variant]"
        );

      if (!vbtn) {
        return;
      }

      e.preventDefault();

      const id =
        parseInt(
          vbtn.getAttribute(
            "data-variant-id"
          ),
          10
        );

      if (!id) {
        return;
      }

      updateVariantSelection(
        id
      );
    }
  );

  /* =========================
     Full Product
  ========================= */

  document.addEventListener(
    "click",
    (e) => {
      const fullBtn =
        e.target.closest(
          "[data-qw-full]"
        );

      if (!fullBtn) {
        return;
      }

      e.preventDefault();

      e.stopPropagation();

      if (
        state.productUrl
      ) {
        window.location.href =
          state.productUrl;
      }
    }
  );

  /* =========================
     Add to cart
  ========================= */

  if (btnAdd) {
    btnAdd.addEventListener(
      "click",
      async (e) => {
        e.preventDefault();

        if (
          !state.selectedVariantId
        ) {
          return;
        }

        const original =
          btnAdd.textContent;

        btnAdd.disabled =
          true;

        btnAdd.textContent =
          "Adding…";

        try {
          await addToCart(
            state.selectedVariantId,
            1
          );

          await refreshMiniCart();

          btnAdd.textContent =
            "Added ✓";

          setTimeout(
            () => {
              btnAdd.textContent =
                original;

              btnAdd.disabled =
                false;
            },
            900
          );

        } catch (err) {
          console.warn(
            "[QW] ATC failed",
            err
          );

          btnAdd.textContent =
            "Error";

          setTimeout(
            () => {
              btnAdd.textContent =
                original;

              btnAdd.disabled =
                false;
            },
            1200
          );
        }
      }
    );
  }

  /* =========================
     Responsive restyling
  ========================= */

  let qwResizeTimer;

  window.addEventListener(
    "resize",
    () => {
      clearTimeout(
        qwResizeTimer
      );

      qwResizeTimer =
        setTimeout(
          () => {
            if (
              modal.classList.contains(
                "is-open"
              )
            ) {
              styleVariantCards();
              styleQuickWeightTop();
            }
          },
          100
        );
    }
  );

  /* =========================
     Cart drawer refresh
  ========================= */

  document.addEventListener(
    "click",
    async (e) => {
      const cartOpen =
        e.target.closest(
          [
            ".js-cart-open",
            "[data-cart-open]",
            ".site-header__cart",
            ".header__icon--cart"
          ].join(", ")
        );

      if (!cartOpen) {
        return;
      }

      try {
        await refreshMiniCart();

      } catch (_) {}
    }
  );

})();